// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — system dialog/report descriptors (D5)
// ═══════════════════════════════════════════════════════════════════════════
//
// Category: "system" — split out of `system.ts` for file-size hygiene
// (CLAUDE.md "Контроль размера файлов": system.ts is already ~1300 lines).
// Covers one-shot dialog-openers and reports that were dispatched via
// literal `upperStart === "..."` comparisons in App.tsx's
// `submitCommandText` before the D5 router→registry unification landed:
//
//   • WBLOCK      — export a block / selection / drawing to a new DXF file.
//     Pure client-side side effect (Blob download) — no EditorCommand.
//   • PRINT/PLOT  — open the print-preview / page-setup-scoped plot dialog.
//     No document mutation — printing does not alter the drawing.
//   • PAGESETUP   — open the per-layout Page Setup Manager.
//   • BCOUNT      — report block-instance counts to the command feed
//     (read-only; no dispatch).
//   • DSETTINGS/DDRMODES/DDRMODE — open the Drafting Settings dialog.
//   • BATCH       — run a JSON array of command strings sequentially
//     through the same dispatcher (orchestration only, no literal of its
//     own — each sub-command is itself resolved/dispatched individually).
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const WBLOCK: CommandDescriptor = {
  id: "WBLOCK",
  category: "system",
  status: "implemented",
  // File-export side effect, not a document mutation — outside the AI
  // drafting safe contour.
  aiCallable: false,
  aliases: { en: ["WBLOCK", "W"], ru: ["ЗПБЛОК"] },
  // Pure client-side export (Blob + anchor download) — no EditorCommand
  // dispatch; the drawing document is read but never mutated.
  commandTypeLiterals: [],
  parameters: [
    {
      name: "source",
      type: "enum",
      enumValues: ["block", "drawing", "objects"],
      required: true,
      description: {
        ru: "Источник экспорта: именованный блок, весь чертёж (в виде блока) или выбранные объекты с базовой точкой.",
        en: "Export source: a named block, the entire drawing (as a block) or the selected objects with a base point.",
      },
    },
    {
      name: "filename",
      type: "string",
      required: true,
      description: {
        ru: "Имя выходного .dxf файла.",
        en: "Output .dxf file name.",
      },
    },
  ],
  help: {
    ru: {
      short: "Записывает блок / чертёж / выделение в новый DXF-файл.",
      long: "Команда ЗПБЛОК (WBLOCK) открывает WriteBlockDialog, где пользователь выбирает источник (именованный блок / весь чертёж / выделенные объекты + базовая точка), единицы вставки и имя файла. При подтверждении wblockExportMutation строит новый самостоятельный DXF-документ и запускает скачивание через Blob — исходный чертёж не изменяется.",
      examples: ["WBLOCK", "W", "ЗПБЛОК"],
      seeAlso: ["BLOCK", "PURGE", "RENAME"],
    },
    en: {
      short: "Writes a block / drawing / selection out to a new DXF file.",
      long: "The WBLOCK command opens WriteBlockDialog, where the user picks a source (named block / entire drawing / selected objects + base point), insertion units, and a file name. On confirm, wblockExportMutation builds a new standalone DXF document and triggers a Blob download — the source drawing is left unchanged.",
      examples: ["WBLOCK", "W"],
      seeAlso: ["BLOCK", "PURGE", "RENAME"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.wblock.label",
    labelKeyEn: "command.wblock.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "W",
  },
  reducerLocations: [],
};

const PRINT: CommandDescriptor = {
  id: "PRINT",
  category: "system",
  status: "implemented",
  // View/output operation — no document mutation, matches the REGEN /
  // REGENALL AI-callable:false precedent (ТЗ §10.1).
  aiCallable: false,
  aliases: { en: ["PRINT", "PLOT"], ru: ["ПЕЧАТЬ"] },
  commandTypeLiterals: [],
  parameters: [],
  help: {
    ru: {
      short: "Открывает диалог печати / предпросмотра плоттера.",
      long: "Команда ПЕЧАТЬ (PRINT/PLOT) открывает диалог настройки печати текущего листа: формат бумаги, область печати, масштаб, таблица стилей печати. Пока диалог открыт, поверх канвы рисуется прямоугольник, отслеживающий печатаемую область. Печать не изменяет геометрию чертежа.",
      examples: ["PRINT", "PLOT", "ПЕЧАТЬ"],
      seeAlso: ["PAGESETUP"],
    },
    en: {
      short: "Opens the print / plot-preview dialog.",
      long: "The PRINT (PLOT) command opens the print-settings dialog for the current layout: paper size, plot area, scale, plot-style table. While the dialog is open, a rectangle tracing the printable area is drawn over the canvas. Printing does not modify the drawing's geometry.",
      examples: ["PRINT", "PLOT"],
      seeAlso: ["PAGESETUP"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.print.label",
    labelKeyEn: "command.print.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [],
};

const PAGESETUP: CommandDescriptor = {
  id: "PAGESETUP",
  category: "system",
  status: "implemented",
  aiCallable: false,
  aliases: { en: ["PAGESETUP"], ru: ["ПАРАМЛИСТ", "ПАРАМЛ"] },
  commandTypeLiterals: [],
  parameters: [],
  help: {
    ru: {
      short: "Открывает диспетчер параметров листов (Page Setup Manager).",
      long: "Команда ПАРАМЛИСТ (PAGESETUP) открывает диспетчер параметров листов: формат бумаги, ориентация, область печати, масштаб, таблица стилей печати, поворот, смещение и поля — на каждый лист (Layout) отдельно. Применяется через собственный store-путь диалога, минуя типизированный Command Engine — известный технический долг, отслеживаемый отдельно от D5.",
      examples: ["PAGESETUP", "ПАРАМЛИСТ", "ПАРАМЛ"],
      seeAlso: ["PRINT", "LAYOUT"],
    },
    en: {
      short: "Opens the per-layout Page Setup Manager.",
      long: "The PAGESETUP command opens the Page Setup Manager: paper size, orientation, plot area, scale, plot-style table, rotation, offset and margins — configured per layout (paper space). Applied through the dialog's own store path rather than the typed Command Engine — a known piece of tech debt tracked separately from D5.",
      examples: ["PAGESETUP"],
      seeAlso: ["PRINT", "LAYOUT"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.pagesetup.label",
    labelKeyEn: "command.pagesetup.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [],
};

const BCOUNT: CommandDescriptor = {
  id: "BCOUNT",
  category: "system",
  status: "implemented",
  // Read-only report — not AI-callable (query, not a creation/modification
  // action).
  aiCallable: false,
  aliases: { en: ["BCOUNT"], ru: ["СЧЕТБЛОК"] },
  commandTypeLiterals: [],
  parameters: [],
  help: {
    ru: {
      short: "Выводит в командную строку количество экземпляров каждого блока.",
      long: "Команда СЧЕТБЛОК (BCOUNT) подсчитывает количество INSERT-экземпляров каждого именованного блока во всём документе (по всем пространствам) и печатает сводку в ленту команд. Чисто информационная команда — ничего не изменяет и не диспетчеризует EditorCommand.",
      examples: ["BCOUNT", "СЧЕТБЛОК"],
      seeAlso: ["PURGE", "BLOCK"],
    },
    en: {
      short: "Prints the instance count of every block to the command feed.",
      long: "The BCOUNT command counts INSERT instances of every named block across the whole document (all spaces) and prints a summary to the command feed. A purely informational command — it changes nothing and dispatches no EditorCommand.",
      examples: ["BCOUNT"],
      seeAlso: ["PURGE", "BLOCK"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.bcount.label",
    labelKeyEn: "command.bcount.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [],
};

const DSETTINGS: CommandDescriptor = {
  id: "DSETTINGS",
  category: "system",
  status: "implemented",
  aiCallable: false,
  aliases: { en: ["DSETTINGS", "DDRMODES", "DDRMODE"], ru: [] },
  commandTypeLiterals: [],
  parameters: [],
  help: {
    ru: {
      short: "Открывает диалог «Режимы рисования» (шаг/сетка, привязки, отслеживание).",
      long: "Команда DSETTINGS (DDRMODES/DDRMODE) открывает вкладочный диалог Drafting Settings: шаг и сетка (SNAP/GRID), полярное отслеживание, объектная привязка (OSNAP) и динамический ввод. Настройки записываются в соответствующие sysvar через отдельные controls внутри диалога, минуя прямой EditorCommand на уровне самой команды открытия.",
      examples: ["DSETTINGS", "DDRMODES"],
      seeAlso: [],
    },
    en: {
      short: "Opens the Drafting Settings dialog (snap/grid, osnap, tracking).",
      long: "The DSETTINGS (DDRMODES/DDRMODE) command opens the tabbed Drafting Settings dialog: Snap and Grid, Polar Tracking, Object Snap (OSNAP) and Dynamic Input. Settings are written to their sysvars through the dialog's own controls, not through a single EditorCommand for the open verb itself.",
      examples: ["DSETTINGS"],
      seeAlso: [],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.dsettings.label",
    labelKeyEn: "command.dsettings.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [],
};

const BATCH: CommandDescriptor = {
  id: "BATCH",
  category: "system",
  status: "implemented",
  // Orchestration wrapper around other commands — not itself AI-callable;
  // each sub-command it replays is validated/dispatched on its own merits.
  aiCallable: false,
  aliases: { en: ["BATCH"], ru: [] },
  // No literal of its own — BATCH re-enters the SAME dispatcher for every
  // sub-command string in its JSON array, so each sub-command's own
  // descriptor/literal already accounts for the resulting dispatch.
  commandTypeLiterals: [],
  parameters: [
    {
      name: "commands",
      type: "object",
      required: true,
      description: {
        ru: 'JSON-массив строк команд, например BATCH ["LINE","0,0","100,0",""].',
        en: 'JSON array of command strings, e.g. BATCH ["LINE","0,0","100,0",""].',
      },
    },
  ],
  help: {
    ru: {
      short: "Выполняет JSON-массив команд последовательно (для AI/скриптов).",
      long: "Команда BATCH принимает JSON-массив строк (`BATCH [\"cmd1\",\"cmd2\",...]`) и прогоняет каждую строку через тот же диспетчер submitCommandText с source=\"batch\" (что отключает анимацию ZOOM и делает переходы мгновенными). Между командами дожидается сигнала рендера или 300мс таймаута. Используется AI-агентом и e2e-скриптами для многошаговых сценариев.",
      examples: ['BATCH ["LINE","0,0","100,0",""]'],
      seeAlso: [],
    },
    en: {
      short: "Runs a JSON array of commands sequentially (for AI / scripts).",
      long: 'The BATCH command accepts a JSON array of strings (`BATCH ["cmd1","cmd2",...]`) and replays each one through the same submitCommandText dispatcher with source="batch" (which disables ZOOM animation and makes transitions instant). It waits for a render signal or a 300ms timeout between commands. Used by the AI agent and e2e scripts for multi-step scenarios.',
      examples: ['BATCH ["LINE","0,0","100,0",""]'],
      seeAlso: [],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.batch.label",
    labelKeyEn: "command.batch.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [],
};

const PROPERTY: CommandDescriptor = {
  id: "PROPERTY",
  category: "system",
  status: "implemented",
  // AI-agent-friendly unified property setter — routes to the SAME
  // dynamic/set + chprop/apply literals DYNSET / CHPROP already claim, so
  // the underlying mutation is exactly as safe-contour-gated as those.
  aiCallable: true,
  aliases: { en: ["PROPERTY"], ru: ["СВОЙСТВО"] },
  commandTypeLiterals: ["dynamic/set", "chprop/apply"],
  parameters: [
    {
      name: "entityId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор сущности, чьё свойство изменяется.",
        en: "Id of the entity whose property is changed.",
      },
    },
    {
      name: "propertyName",
      type: "string",
      required: true,
      description: {
        ru: "Имя изменяемого свойства: либо customProperty динамического блока, либо стандартное (Layer/Color/Linetype/Linetype scale/Lineweight/Transparency).",
        en: "Name of the property to change: either a dynamic-block customProperty or a standard one (Layer/Color/Linetype/Linetype scale/Lineweight/Transparency).",
      },
    },
    {
      name: "rawValue",
      type: "string",
      required: true,
      description: {
        ru: "Новое значение свойства (текст до валидации/парсинга конкретным планировщиком).",
        en: "New property value (raw text, validated/parsed by the specific planner).",
      },
    },
  ],
  help: {
    ru: {
      short: "PROPERTY <id> <имя> <значение> — единая команда изменения свойства.",
      long: "Команда PROPERTY (СВОЙСТВО) — унифицированная точка входа, удобная для AI-агента и скриптов: planPropertyDispatch определяет, относится ли <имя> к customProperty динамического блока (диспетчеризуется dynamic/set) или к стандартному свойству сущности (диспетчеризуется chprop/apply). Неизвестное имя свойства или несуществующая сущность возвращают понятную ошибку в ленту команд без диспетчеризации.",
      examples: ["PROPERTY ins-1 Layer Walls", "СВОЙСТВО ins-1 Width 250"],
      seeAlso: ["CHPROP", "DYNSET", "MATCHPROP"],
    },
    en: {
      short: "PROPERTY <id> <name> <value> — unified property-change entrypoint.",
      long: "The PROPERTY command is a unified entrypoint convenient for the AI agent and scripts: planPropertyDispatch decides whether <name> is a dynamic-block customProperty (dispatches dynamic/set) or a standard entity property (dispatches chprop/apply). An unknown property name or missing entity returns a clear error to the command feed without dispatching anything.",
      examples: ["PROPERTY ins-1 Layer Walls"],
      seeAlso: ["CHPROP", "DYNSET", "MATCHPROP"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.property.label",
    labelKeyEn: "command.property.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [],
};

export const SYSTEM_DIALOG_DESCRIPTORS: readonly CommandDescriptor[] = [
  WBLOCK,
  PRINT,
  PAGESETUP,
  BCOUNT,
  DSETTINGS,
  BATCH,
  PROPERTY,
];
